import {
  Pressable,
  ScrollView, StyleSheet, Text, View,
} from 'react-native';
import { useEffect, useState } from 'react';
import Colors from '../../constants/Colors';
import { UserPermission } from '../../constants/Types';
import TickIcon from '../TickIcon';
import CloseIconSmall from '../12pxIcons/CloseIconSmall';
import isMobile from '../IsMobile';
import PressableStyled from '../PressableStyled';
import EditIcon24 from '../24pxIcons/EditIcon24';
import {
  DEFAULT_ROLE_READ_PERMISSIONS,
  ensureDefaultRoleReadPermissions,
} from '../../constants/UserPermissions';

export default function RoleMatrix(props) {
  const enforceTemplatePermissionDependency = (permissions = []) => {
    const normalizedPermissions = [...permissions];

    if (
      normalizedPermissions.includes(UserPermission.TEMPLATES_CREATE)
      && !normalizedPermissions.includes(UserPermission.TEMPLATES_UPDATE)
    ) {
      normalizedPermissions.push(UserPermission.TEMPLATES_UPDATE);
    }

    return normalizedPermissions;
  };

  const [editingIndex, setEditingIndex] = useState(null);
  const [currentPermissions, setCurrentPermissions] = useState(
    enforceTemplatePermissionDependency(
      ensureDefaultRoleReadPermissions(props?.selectedRole?.permissions || []),
    ),
  );

  useEffect(() => {
    setCurrentPermissions(enforceTemplatePermissionDependency(
      ensureDefaultRoleReadPermissions(
        props?.selectedRole?.permissions || [],
      ),
    ));
  }, [props.selectedRole]);

  const togglePermission = (permission) => {
    const newPermissions = [...currentPermissions];
    const index = newPermissions.indexOf(permission);
    const base = permission.substring(0, permission.lastIndexOf('_'));

    if (index > -1) {
      if (DEFAULT_ROLE_READ_PERMISSIONS.includes(permission)) return;

      newPermissions.splice(index, 1);

      if (permission === UserPermission.TEMPLATES_UPDATE) {
        const templatesCreateIndex = newPermissions.indexOf(UserPermission.TEMPLATES_CREATE);
        if (templatesCreateIndex > -1) newPermissions.splice(templatesCreateIndex, 1);
      }

      if (permission.endsWith('READ')) {
        [`${base}_CREATE`, `${base}_UPDATE`, `${base}_DELETE`].forEach((dependentPermission) => {
          const dependentIndex = newPermissions.indexOf(dependentPermission);
          if (dependentIndex > -1) newPermissions.splice(dependentIndex, 1);
        });
      }
    } else {
      newPermissions.push(permission);

      if (
        permission.endsWith('CREATE')
        || permission.endsWith('UPDATE')
        || permission.endsWith('DELETE')
      ) {
        const readPermission = `${base}_READ`;

        if (!newPermissions.includes(readPermission)) {
          newPermissions.push(readPermission);
        }
      }
    }
    setCurrentPermissions(enforceTemplatePermissionDependency(newPermissions));
  };

  const ColumnHeader = ({ text, flex = 1 }) => (
    <Text
      style={{
        fontSize: 10,
        marginBottom: 10,
        flex: flex || 1,
        fontFamily: 'Roboto_500Medium',
        color: Colors.light.hereworksBlack400,
      }}
    >
      {text}
    </Text>
  );

  const HasPermission = ({ permission, index = null }) => (
    <View style={{ flex: 1, alignItems: 'flex-start' }}>
      <Pressable
        disabled={!props.canUpdateRoles || editingIndex !== index}
        onPress={() => togglePermission(permission)}
        style={{
          justifyContent: 'center',
          alignItems: 'flex-start',
          height: 20,
          paddingHorizontal: 2,
          borderWidth: 1,
          borderColor: editingIndex === index ? Colors.light.hereworksBlue100 : 'transparent',
          borderRadius: 4,
        }}
      >
        {currentPermissions.includes(permission) ? (
          <TickIcon />
        ) : (
          <CloseIconSmall color={Colors.light.hereworksRed500} />
        )}
      </Pressable>
    </View>
  );

  const ColumnHeaders = () => (
    <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {!isMobile && <ColumnHeader text="PERMISSIONS" flex={2} />}
        <ColumnHeader text="CREATE" />
        <ColumnHeader text="VIEW" />
        <ColumnHeader text="EDIT" />
        <ColumnHeader text="DELETE" />
      </View>
  );

  const EditRole = ({ index }) => (editingIndex === index ? <PressableStyled
  style={{
    position: 'absolute',
    top: 6,
    right: 6,
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: Colors.light.hereworksBlue500,
    borderRadius: 4,
  }} onPress={async () => {
    const saveSucceeded = await props.onSave(
      props.selectedRole?.name,
      props.selectedRole?.description,
      currentPermissions,
      props.selectedRole?.id,
    );

    if (saveSucceeded) {
      setEditingIndex(null);
    }
  }}>
      <Text style={{ color: '#fff' }}>Save</Text>
    </PressableStyled> : <PressableStyled
      onPress={() => setEditingIndex(index)}
      style={{
        position: 'absolute',
        top: 6,
        right: 6,
        flexDirection: 'row',
        alignItems: 'center',
        padding: 8,
      }}
    >
      <EditIcon24 />
    </PressableStyled>);

  if (isMobile) {
    return (
    <ScrollView>
      <View style={[styles.mobileSection,
        editingIndex === 0 ? { borderColor: Colors.light.hereworksBlue500 } : {},
      ]}>
        <Text style={styles.rowTitleMobile}>{'Tickets'}</Text>
        <ColumnHeaders />
        <View style={styles.rowMobile}>
          <HasPermission permission={UserPermission.TICKETS_CREATE} index={0} />
          <HasPermission permission={UserPermission.TICKETS_READ} index={0} />
          <HasPermission permission={UserPermission.TICKETS_UPDATE} index={0} />
          <HasPermission permission={UserPermission.TICKETS_DELETE} index={0} />
        </View>
        {props.canUpdateRoles && <EditRole index={0} />}
      </View>
      <View style={[styles.mobileSection,
        editingIndex === 1 ? { borderColor: Colors.light.hereworksBlue500 } : {},
      ]}>
        <Text style={styles.rowTitleMobile}>{'Templates'}</Text>
        <ColumnHeaders />
        <View style={styles.rowMobile}>
          <HasPermission permission={UserPermission.TEMPLATES_CREATE} index={1} />
          <HasPermission permission={UserPermission.TEMPLATES_READ} index={1} />
          <HasPermission permission={UserPermission.TEMPLATES_UPDATE} index={1} />
          <HasPermission permission={UserPermission.TEMPLATES_DELETE} index={1} />
        </View>
        {props.canUpdateRoles && <EditRole index={1} />}
      </View>
      <View style={[styles.mobileSection,
        editingIndex === 2 ? { borderColor: Colors.light.hereworksBlue500 } : {},
      ]}>
        <Text style={styles.rowTitleMobile}>{'Assets'}</Text>
        <ColumnHeaders />
        <View style={styles.rowMobile}>
          <HasPermission permission={UserPermission.ASSETS_CREATE} index={2} />
          <HasPermission permission={UserPermission.ASSETS_READ} index={2} />
          <HasPermission permission={UserPermission.ASSETS_UPDATE} index={2} />
          <HasPermission permission={UserPermission.ASSETS_DELETE} index={2} />
        </View>
        {props.canUpdateRoles && <EditRole index={2} />}
      </View>
      <View style={[styles.mobileSection,
        editingIndex === 3 ? { borderColor: Colors.light.hereworksBlue500 } : {},
      ]}>
        <Text style={styles.rowTitleMobile}>{'User Groups'}</Text>
        <ColumnHeaders />
        <View style={styles.rowMobile}>
          <HasPermission permission={UserPermission.USER_GROUPS_CREATE} index={3} />
          <HasPermission permission={UserPermission.USER_GROUPS_READ} index={3} />
          <HasPermission permission={UserPermission.USER_GROUPS_UPDATE} index={3} />
          <HasPermission permission={UserPermission.USER_GROUPS_DELETE} index={3} />
        </View>
        {props.canUpdateRoles && <EditRole index={3} />}
      </View>
      <View style={[styles.mobileSection,
        editingIndex === 4 ? { borderColor: Colors.light.hereworksBlue500 } : {},
      ]}>
        <Text style={styles.rowTitleMobile}>{'Users'}</Text>
        <ColumnHeaders />
        <View style={styles.rowMobile}>
          <HasPermission permission={UserPermission.USERS_CREATE} index={4} />
          <HasPermission permission={UserPermission.USERS_READ} index={4} />
          <HasPermission permission={UserPermission.USERS_UPDATE} index={4} />
          <HasPermission permission={UserPermission.USERS_DELETE} index={4} />
        </View>
        {props.canUpdateRoles && <EditRole index={4} />}
      </View>
      <View style={[styles.mobileSection,
        editingIndex === 5 ? { borderColor: Colors.light.hereworksBlue500 } : {},
      ]}>
        <Text style={styles.rowTitleMobile}>{'Locations'}</Text>
        <ColumnHeaders />
        <View style={styles.rowMobile}>
          <HasPermission permission={UserPermission.LOCATIONS_CREATE} index={5} />
          <HasPermission permission={UserPermission.LOCATIONS_READ} index={5} />
          <HasPermission permission={UserPermission.LOCATIONS_UPDATE} index={5} />
          <HasPermission permission={UserPermission.LOCATIONS_DELETE} index={5} />
        </View>
        {props.canUpdateRoles && <EditRole index={5} />}
      </View>
    </ScrollView>
    );
  }

  return (
    <>
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottomWidth: 1,
          borderColor: Colors.light.hereworksWhite500,
        }}
      >
        <ColumnHeader text="PERMISSIONS" flex={2} />
        <ColumnHeader text="CREATE" />
        <ColumnHeader text="VIEW" />
        <ColumnHeader text="EDIT" />
        <ColumnHeader text="DELETE" />
      </View>
      <View style={styles.row}>
        <Text style={styles.rowTitle}>{'Tickets'}</Text>
        <HasPermission permission={UserPermission.TICKETS_CREATE} />
        <HasPermission permission={UserPermission.TICKETS_READ} />
        <HasPermission permission={UserPermission.TICKETS_UPDATE} />
        <HasPermission permission={UserPermission.TICKETS_DELETE} />
      </View>
      <View style={styles.row}>
        <Text style={styles.rowTitle}>{'Templates'}</Text>
        <HasPermission permission={UserPermission.TEMPLATES_CREATE} />
        <HasPermission permission={UserPermission.TEMPLATES_READ} />
        <HasPermission permission={UserPermission.TEMPLATES_UPDATE} />
        <HasPermission permission={UserPermission.TEMPLATES_DELETE} />
      </View>
      <View style={styles.row}>
        <Text style={styles.rowTitle}>{'Assets'}</Text>
        <HasPermission permission={UserPermission.ASSETS_CREATE} />
        <HasPermission permission={UserPermission.ASSETS_READ} />
        <HasPermission permission={UserPermission.ASSETS_UPDATE} />
        <HasPermission permission={UserPermission.ASSETS_DELETE} />
      </View>
      <View style={styles.row}>
        <Text style={styles.rowTitle}>{'User Groups'}</Text>
        <HasPermission permission={UserPermission.USER_GROUPS_CREATE} />
        <HasPermission permission={UserPermission.USER_GROUPS_READ} />
        <HasPermission permission={UserPermission.USER_GROUPS_UPDATE} />
        <HasPermission permission={UserPermission.USER_GROUPS_DELETE} />
      </View>
      <View style={styles.row}>
        <Text style={styles.rowTitle}>{'Users'}</Text>
        <HasPermission permission={UserPermission.USERS_CREATE} />
        <HasPermission permission={UserPermission.USERS_READ} />
        <HasPermission permission={UserPermission.USERS_UPDATE} />
        <HasPermission permission={UserPermission.USERS_DELETE} />
      </View>
      <View style={styles.row}>
        <Text style={styles.rowTitle}>{'Locations'}</Text>
        <HasPermission permission={UserPermission.LOCATIONS_CREATE} />
        <HasPermission permission={UserPermission.LOCATIONS_READ} />
        <HasPermission permission={UserPermission.LOCATIONS_UPDATE} />
        <HasPermission permission={UserPermission.LOCATIONS_DELETE} />
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 8,
    padding: 10,
    marginVertical: 12,
    borderColor: Colors.light.hereworksBlue100,
  },
  rowMobile: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 10,
    marginBottom: 12,
    borderTopWidth: 1,
    borderColor: Colors.light.hereworksWhite500,
  },
  rowTitle: {
    fontSize: 14,
    marginBottom: 10,
    flex: 2,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
  },
  rowTitleMobile: {
    fontSize: 14,
    marginBottom: 10,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
  },
  mobileSection: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 16,
    marginVertical: 4,
    borderColor: Colors.light.hereworksBlue100,
    backgroundColor: '#fff',
  },
});
